import React from 'react';
import styles from './StaticMap.module.scss';
import { SFThemeType } from 'sfui';
import {
  GOOGLE_STATIC_DAY_MODE_URL,
  GOOGLE_STATIC_NIGHT_MODE_URL
} from '../../../../Constants/Maps';

import { GeoLocationCoords, AppearanceContext } from 'ui-smartforce-settings';

const IMG_WIDTH = 640;
const IMG_HEIGHT = 640;
const ZOOM = 15;
const SCALE = 2;
const MARKER_DAY_MODE_URL = `${process.env.REACT_APP_IMAGES_BASE_URL}/markers/DM_map-marker.png`;
const MARKER_NIGHT_MODE_URL = `${process.env.REACT_APP_IMAGES_BASE_URL}/markers/NM_map-marker.png`;

// Encode one signed integer (delta) into the polyline chunk string
function encodeSignedNumber(value: number): string {
  // Step 1: left-shift the value by 1 bit
  // Step 2: if negative, invert all bits (bitwise NOT)
  // This encodes the sign into the low bit per Google's algorithm
  let encoded = value < 0 ? ~(value << 1) : value << 1;

  const chars: number[] = [];

  // Step 3: emit 5-bit chunks, setting the 0x20 (continuation) bit
  while (encoded >= 0x20) {
    const chunk = (0x20 | (encoded & 0x1f)) + 63; // set continuation, map to ASCII
    chars.push(chunk);
    encoded >>= 5;
  }

  // Step 4: final chunk (no continuation bit), map to ASCII and push
  chars.push(encoded + 63);

  // Convert numeric char codes to a JS string
  return String.fromCharCode(...chars);
}

// Encode an array of { latitude, longitude } into a Google encoded polyline string
export function encodePolyline(
  points: { latitude: number; longitude: number }[]
): string {
  let lastLat = 0;
  let lastLng = 0;
  const resultParts: string[] = [];

  for (let i = 0; i < points.length; i++) {
    const lat = Math.round(points[i].latitude * 1e5);
    const lng = Math.round(points[i].longitude * 1e5);

    const dLat = lat - lastLat;
    const dLng = lng - lastLng;

    resultParts.push(encodeSignedNumber(dLat));
    resultParts.push(encodeSignedNumber(dLng));

    lastLat = lat;
    lastLng = lng;
  }

  return resultParts.join('');
}

const getMapUrl = (
  themeType: SFThemeType,
  iconUrl: string,
  width: number,
  height: number,
  scale: number,
  position?: GeoLocationCoords,
  zoom?: number,
  areas?: GeoLocationCoords[][]
) => {
  let url: string =
    themeType === 'day'
      ? GOOGLE_STATIC_DAY_MODE_URL
      : GOOGLE_STATIC_NIGHT_MODE_URL;

  if (position) url += `&center=${position.latitude},${position.longitude}`;
  if (zoom) url += `&zoom=${zoom}`;

  url += `&size=${width}x${height}&scale=${scale}`;

  if (!areas || areas.length === 0) {
    if (position)
      url += `&markers=icon:${iconUrl}|anchor:center|${position.latitude},${position.longitude}`;
  } else {
    areas.forEach((a: GeoLocationCoords[]) => {
      if (!a || a.length === 0) return;

      const encoded = encodePolyline(a);
      const pathDescriptor =
        themeType === 'day'
          ? `fillcolor:0x0066ff1a|color:0x0066ff|weight:3|enc:${encoded}`
          : `fillcolor:0x80C6FF33|color:0x80C6FF|weight:3|enc:${encoded}`;
      url += `&path=${encodeURIComponent(pathDescriptor)}`;
    });
  }
  url += `&key=AIzaSyDET6OJJuN80emEkb8VMTd-K-XaXDrgAjI`;

  return url;
};

/**
 * position or areas must be provided as prop value
 */
export interface StaticMapProps {
  className?: string;
  position?: GeoLocationCoords;
  zoom?: number;
  areas?: GeoLocationCoords[][];
}

export const StaticMap = ({
  className = '',
  position,
  zoom,
  areas
}: StaticMapProps): React.ReactElement<StaticMapProps> => {
  const { themeType } = React.useContext(AppearanceContext);

  const iconUrl: string =
    themeType === 'day' ? MARKER_DAY_MODE_URL : MARKER_NIGHT_MODE_URL;

  const googleUrl: string = getMapUrl(
    themeType,
    iconUrl,
    IMG_WIDTH,
    IMG_HEIGHT,
    SCALE,
    position,
    zoom || ZOOM,
    areas
  );

  return (
    <div
      className={`${styles.staticMap} ${className}`}
      style={{ backgroundImage: `url(${googleUrl})` }}
    ></div>
  );
};
